import * as plurals from 'make-plural/plurals'
import { i18n } from '@lingui/core'
import { CompiledMessage } from '@lingui/core/cjs/i18n'
import { camelCase, clone, kebabCase } from 'lodash'

import { SupportedLocalesEnum } from '../graphql/public/PublicSchemaTypes'

import {
  TranslationMessagesDocument,
  TranslationMessagesQuery,
  TranslationMessagesQueryVariables,
  DefaultLocaleQuery,
  DefaultLocaleQueryVariables,
  DefaultLocaleDocument
} from '../public-api/i18n.codegen'
import { apolloClientPublic } from '../graphql/apolloClient'

import { setDateLocale } from 'shared/setDateLocale'
import { linguiCompileMessage } from '../../../shared/linguiCompileMessage'
import {
  CustomTranslationMessagesDocument,
  CustomTranslationMessagesQuery,
  CustomTranslationMessagesQueryVariables
} from './i18n.codegen'

// specify blackList of environments
export const isLocalizationEnabled = !['prod', 'uat'].includes(
  process.env.LOOOP_ENVIRONMENT as string
)

export const fetchCustomI18n = async (locale: SupportedLocalesEnum) => {
  const messages: Record<string, CompiledMessage> = {}
  const {
    data: {
      organisation: { customTranslationMessages }
    }
  } = await apolloClientPublic.query<
    CustomTranslationMessagesQuery,
    CustomTranslationMessagesQueryVariables
  >({
    query: CustomTranslationMessagesDocument,
    variables: { locale },
    fetchPolicy: 'cache-first'
  })

  customTranslationMessages.forEach(({ term, translation }) => {
    messages[term] = linguiCompileMessage(translation)
  })

  return messages
}

function isSupportedLocale(locale) {
  return Object.keys(SupportedLocalesEnum).includes(locale)
}

export const detectUILocale = async (
  locale?: SupportedLocalesEnum | null
): Promise<SupportedLocalesEnum> => {
  if (!locale) {
    locale = camelCase(getBrowserLocale()) as SupportedLocalesEnum

    if (!isSupportedLocale(locale)) {
      const { data } = await apolloClientPublic.query<
        DefaultLocaleQuery,
        DefaultLocaleQueryVariables
      >({
        query: DefaultLocaleDocument,
        fetchPolicy: 'cache-first'
      })

      locale =
        data?.organisation?.default_user_locale || SupportedLocalesEnum.enGb
    }
  }

  return locale
}

export enum LocaleMessagesOverloadSourceEnum {
  NONE = 'NONE', // used for receptionist-we cannot load custom locales there
  POEDITOR = 'POEDITOR',
  ORG_CUSTOM = 'ORG_CUSTOM'
}

export const loadLocaleMessages = async ({
  locale,
  overloadSource = LocaleMessagesOverloadSourceEnum.ORG_CUSTOM
}: {
  locale?: SupportedLocalesEnum | null
  overloadSource?: LocaleMessagesOverloadSourceEnum
} = {}) => {
  const detectedLocale = await detectUILocale(locale)

  await setDateLocale(detectedLocale)

  const kebabCaseLocale = kebabCase(detectedLocale)
  let messagesWithOverloads = {}

  // don't do anything if locale is the same
  if (kebabCaseLocale && i18n.locale && kebabCaseLocale === i18n.locale) {
    return
  }

  if (overloadSource === LocaleMessagesOverloadSourceEnum.POEDITOR) {
    const {
      data: { translationMessages }
    } = await apolloClientPublic.query<
      TranslationMessagesQuery,
      TranslationMessagesQueryVariables
    >({
      query: TranslationMessagesDocument,
      variables: { locale: detectedLocale },
      fetchPolicy: 'cache-first'
    })

    messagesWithOverloads = translationMessages
  } else if (overloadSource === LocaleMessagesOverloadSourceEnum.ORG_CUSTOM) {
    messagesWithOverloads = await fetchCustomI18n(detectedLocale)
  }

  if (!i18n._localeData[kebabCaseLocale]) {
    const twoLetterCode = detectedLocale.substr(0, 2) as string

    i18n.loadLocaleData(kebabCaseLocale, { plurals: plurals[twoLetterCode] })
  }

  if (!i18n._messages[kebabCaseLocale] || !i18n.locale) {
    const { messages } = await import(
      `../locale/${kebabCaseLocale}/messages.ts`
    )
    const clonedMessages = clone(messages)
    i18n.load(
      kebabCaseLocale,
      Object.assign(clonedMessages, messagesWithOverloads)
    )
  }

  i18n.activate(kebabCaseLocale)
  return detectedLocale
}

export function getBrowserLocale(): string {
  const navigator: NavigatorLanguage = window.navigator
  const browserLocale: string = navigator.languages
    ? navigator.languages[0]
    : navigator.language ||
      // IE fallback
      navigator['browserLanguage'] ||
      navigator['userLanguage']

  return browserLocale
}
